import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { NewPOComparison } from "@/components/ApprovalsTab";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  FilePdf, UploadSimple, FloppyDisk, Trash, ArrowsClockwise, CheckCircle, Warning,
  Eye, Storefront, CurrencyInr, Calendar, Hash, MagnifyingGlass, Table as TableIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending:  "bg-amber-900/40 text-amber-200 border-amber-700/60",
  approved: "bg-emerald-900/40 text-emerald-200 border-emerald-700/60",
  rejected: "bg-rose-900/40 text-rose-200 border-rose-700/60",
};

function pdfUrlFor(po) {
  // Build absolute URL to the inline PDF endpoint so the <iframe> can fetch it
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/new-pos/${po.id}/pdf`;
}

export default function NewPOUploadTab({ refreshKey, onChange }) {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState(null); // currently previewed PO
  const [view, setView] = useState("upload"); // "upload" | "all"
  const [allItems, setAllItems] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allSearch, setAllSearch] = useState("");
  const [hiddenPO, setHiddenPO] = useState(null);      // /new-pos/lookup fallback result
  const [hiddenLoading, setHiddenLoading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Only show pending POs here — once a PO is approved/rejected on the
      // Approvals tab it disappears from this upload list (kept visible on
      // the Approvals tab via the Approved/Rejected/All filter).
      const r = await api.get("/new-pos", { params: { status: "pending" } });
      setItems(r.data || []);
      if (r.data?.length && !selectedId) {
        setSelectedId(r.data[0].id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load POs");
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { load(); }, [refreshKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = useCallback(async () => {
    setAllLoading(true);
    try {
      // No status filter → returns pending + approved + rejected
      const r = await api.get("/new-pos");
      setAllItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load POs");
    } finally {
      setAllLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "all") loadAll();
  }, [view, refreshKey, loadAll]);

  const filteredAll = useMemo(() => {
    const q = allSearch.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((p) => {
      return [
        p.vendor, p.po_number, p.outlet, p.filename,
        p.status, p.chain_status, p.uploaded_by,
        p.amount != null ? String(p.amount) : "",
        p.po_date, p.uploaded_at,
      ].some((f) => (f || "").toString().toLowerCase().includes(q));
    });
  }, [allItems, allSearch]);

  // When the "All Uploaded POs" search returns nothing locally AND the query
  // looks like a PO number (starts with "PO" or is 8+ chars, mostly digits),
  // call the global /new-pos/lookup endpoint. This surfaces POs that the
  // current user's role-scoped visibility filter is hiding — which is the
  // exact scenario behind "duplicate on upload but I can't find it".
  useEffect(() => {
    setHiddenPO(null);
    const q = allSearch.trim();
    if (!q || filteredAll.length > 0) return;
    const looksLikePO = /^po/i.test(q) || (q.length >= 6 && /^[a-z0-9-]+$/i.test(q));
    if (!looksLikePO) return;
    let cancelled = false;
    setHiddenLoading(true);
    api.get("/new-pos/lookup", { params: { po_number: q } })
      .then((r) => { if (!cancelled) setHiddenPO(r.data); })
      .catch(() => { if (!cancelled) setHiddenPO(null); })
      .finally(() => { if (!cancelled) setHiddenLoading(false); });
    return () => { cancelled = true; };
  }, [allSearch, filteredAll.length]);

  const removePOFromAll = async (po) => {
    if (!window.confirm(`Delete PO ${po.po_number || po.filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/new-pos/${po.id}`);
      toast.success("Deleted");
      setAllItems((prev) => prev.filter((p) => p.id !== po.id));
      setItems((prev) => prev.filter((p) => p.id !== po.id));
      if (typeof onChange === "function") onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let okCount = 0;
    let lastId = null;
    try {
      for (const file of files) {
        if (!file.name.toLowerCase().endsWith(".pdf")) {
          toast.error(`${file.name}: only PDF accepted`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        try {
          const r = await api.post("/uploads/new-po", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          okCount++;
          lastId = r.data.id;
          if (r.data?.ocr_error) {
            toast.warning(`${file.name}: OCR partial — please review fields`);
          } else {
            toast.success(`${file.name}: extracted ${r.data.vendor || "(vendor)"} ${r.data.po_number || ""}`);
          }
        } catch (e) {
          const detail = e?.response?.data?.detail || "upload failed";
          const isDup = e?.response?.status === 409 || String(detail).toLowerCase().startsWith("duplicate");
          if (isDup) {
            toast.error(`⚠ ${file.name}: ${detail}`, { duration: 12000 });
          } else {
            toast.error(`${file.name}: ${detail}`);
          }
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
      if (lastId) setSelectedId(lastId);
      if (okCount > 0 && typeof onChange === "function") onChange();
    }
    if (okCount > 1) toast.success(`Uploaded ${okCount} POs`);
  };

  const removePO = async (po) => {
    if (!window.confirm(`Delete ${po.filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/new-pos/${po.id}`);
      toast.success("Deleted");
      if (selectedId === po.id) setSelectedId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const updateField = async (po, patch) => {
    try {
      await api.patch(`/new-pos/${po.id}`, patch);
      // Optimistic update
      setItems((prev) => prev.map((p) => (p.id === po.id ? { ...p, ...patch } : p)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const selected = items.find((p) => p.id === selectedId) || items[0];

  return (
    <div className="space-y-5" data-testid="new-po-upload-tab">
      {/* Sub-tab bar */}
      <div className="flex items-center gap-1 border-b border-slate-800" data-testid="new-po-subtabs">
        <SubTab
          active={view === "upload"}
          onClick={() => setView("upload")}
          icon={<UploadSimple size={13} weight="bold" />}
          label="Upload & Review"
          testid="new-po-subtab-upload"
        />
        <SubTab
          active={view === "all"}
          onClick={() => setView("all")}
          icon={<TableIcon size={13} weight="bold" />}
          label={`All Uploaded POs${allItems.length ? ` (${allItems.length})` : ""}`}
          testid="new-po-subtab-all"
        />
      </div>

      {view === "all" ? (
        <AllPOsView
          items={filteredAll}
          totalCount={allItems.length}
          loading={allLoading}
          search={allSearch}
          setSearch={setAllSearch}
          onRefresh={loadAll}
          onDelete={removePOFromAll}
          hiddenPO={hiddenPO}
          hiddenLoading={hiddenLoading}
        />
      ) : (
        <>
          {/* Header strip */}
          <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2">
            <FilePdf size={22} weight="duotone" className="text-rose-300" />
            New PO upload for approval
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Drop a PO PDF. Gemini Vision auto-extracts <span className="text-cyan-300 font-semibold">vendor</span>,
            <span className="text-cyan-300 font-semibold"> PO #</span>,
            <span className="text-cyan-300 font-semibold"> date</span>,
            <span className="text-cyan-300 font-semibold"> amount</span>, and
            <span className="text-cyan-300 font-semibold"> outlet</span>. Review the side-by-side preview, edit if
            needed. Final approval happens in the <span className="text-amber-200 font-semibold">Approvals → New PO Approvals</span> tab.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="new-po-file-input"
            disabled={uploading}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => load()}
            disabled={loading || uploading}
            className="h-9 rounded-sm border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="new-po-refresh-btn"
            title="Reload"
          >
            <ArrowsClockwise size={14} weight="bold" />
          </Button>
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="h-9 rounded-sm bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="new-po-upload-btn"
          >
            <UploadSimple size={14} weight="bold" className="mr-1.5" />
            {uploading ? "Uploading…" : "Upload New PO"}
          </Button>
        </div>
      </div>

          {/* Body — split view */}
          {loading ? (
            <div className="text-center py-12 text-slate-500 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyState onUpload={() => fileInputRef.current?.click()} uploading={uploading} />
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
              {/* Left: uploaded POs list */}
              <div className="xl:col-span-5 space-y-2" data-testid="new-po-list">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center justify-between">
                  <span>Uploaded POs ({items.length})</span>
                  <span className="text-slate-600">click to preview</span>
                </div>
                <div className="space-y-1.5 max-h-[700px] overflow-y-auto pr-1">
                  {items.map((po) => (
                    <POListRow
                      key={po.id}
                      po={po}
                      active={po.id === selected?.id}
                      onSelect={() => setSelectedId(po.id)}
                      onDelete={() => removePO(po)}
                    />
                  ))}
                </div>
              </div>

              {/* Right: side-by-side preview + edit form */}
              <div className="xl:col-span-7" data-testid="new-po-preview">
                {selected ? (
                  <div className="space-y-3">
                    <POPreviewPanel
                      po={selected}
                      onChange={(patch) => updateField(selected, patch)}
                    />
                    <div className="rounded-md border border-cyan-900/60 overflow-hidden" data-testid="new-po-upload-comparison">
                      <div className="px-4 py-2 bg-slate-900 text-[10px] uppercase tracking-wider font-bold text-cyan-200">
                        Price comparison · this vendor's history + other vendors
                      </div>
                      <NewPOComparison poId={selected.id} refreshKey={`${(selected.line_items || []).length}-${selected.vendor}`} />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500 text-sm">Select a PO to preview</div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SubTab({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={
        "px-4 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-2 transition-colors " +
        (active
          ? "text-cyan-200 border-b-2 border-cyan-400 -mb-px"
          : "text-slate-500 hover:text-slate-200 border-b-2 border-transparent")
      }
    >
      {icon} {label}
    </button>
  );
}

function AllPOsView({ items, totalCount, loading, search, setSearch, onRefresh, onDelete, hiddenPO, hiddenLoading }) {
  return (
    <div className="space-y-3" data-testid="new-po-all-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2">
            <TableIcon size={22} weight="duotone" className="text-cyan-300" />
            All Uploaded POs
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Every PO that&apos;s been uploaded — pending, approved, or rejected. Search across vendor, PO number, outlet, filename, or amount. Delete removes the PO and its file permanently.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onRefresh}
          disabled={loading}
          className="h-9 rounded-sm border-slate-700 text-slate-300 hover:bg-slate-800 shrink-0"
          data-testid="new-po-all-refresh-btn"
          title="Reload"
        >
          <ArrowsClockwise size={14} weight="bold" />
        </Button>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search vendor, PO #, outlet, filename, amount…"
          className="h-9 pl-9 bg-slate-900 border-slate-700 text-sm"
          data-testid="new-po-all-search"
        />
      </div>

      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        {loading ? "Loading…" : `Showing ${items.length} of ${totalCount}`}
      </div>

      {/* Hidden-PO reveal card — shows when the list is empty but the search
          matches a real PO your role isn't allowed to see directly. This is
          the fix for "duplicate on upload but can't find it anywhere". */}
      {items.length === 0 && !loading && (hiddenLoading || hiddenPO) && (
        <div
          className="border border-amber-800/60 bg-amber-950/30 rounded-md p-4 text-sm text-amber-100"
          data-testid="new-po-hidden-card"
        >
          {hiddenLoading && (
            <div className="text-amber-300 text-xs">Looking up &ldquo;{search}&rdquo; across all POs…</div>
          )}
          {hiddenPO && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Warning size={16} weight="fill" className="text-amber-300" />
                <div className="font-bold text-amber-100">This PO exists but is hidden from your role&apos;s view</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5 text-[11.5px]">
                <div><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">PO #</span><span className="text-amber-50 font-mono">{hiddenPO.po_number || "—"}</span></div>
                <div><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Vendor</span><span className="text-amber-50">{hiddenPO.vendor || "—"}</span></div>
                <div><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Outlet</span><span className="text-amber-50">{hiddenPO.outlet || "—"}</span></div>
                <div><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Amount</span><span className="text-amber-50 font-mono tabular-nums">{fmtINR(hiddenPO.amount || 0)}</span></div>
                <div><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">PO Date</span><span className="text-amber-50">{fmtDate(hiddenPO.po_date)}</span></div>
                <div><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Status</span><span className="text-amber-50 uppercase">{(hiddenPO.chain_status || hiddenPO.status || "pending").replace("_", " ")}</span></div>
                <div><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Stage</span><span className="text-amber-50">{hiddenPO.current_level ? `L${hiddenPO.current_level} / ${hiddenPO.total_levels || "?"}` : "—"}</span></div>
                <div><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Awaiting</span><span className="text-amber-50">{hiddenPO.pending_role_label || "—"}</span></div>
                <div className="col-span-2"><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Uploaded By</span><span className="text-amber-50 font-mono text-[10.5px]">{hiddenPO.uploaded_by || "—"}</span></div>
                <div className="col-span-2"><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Uploaded At</span><span className="text-amber-50 font-mono text-[10.5px]">{(hiddenPO.uploaded_at || "").slice(0, 19).replace("T", " ")}</span></div>
                {Array.isArray(hiddenPO.pending_approver_emails) && hiddenPO.pending_approver_emails.length > 0 && (
                  <div className="col-span-4"><span className="text-amber-400/70 uppercase tracking-wider text-[9.5px] font-bold block">Nudge These Approvers</span><span className="text-amber-50 font-mono text-[10.5px]">{hiddenPO.pending_approver_emails.join(", ")}</span></div>
                )}
              </div>
              <div className="mt-3 text-[10.5px] text-amber-200/80 italic">
                To make this appear in your list, ask the pending role above (or an Owner / Admin) to approve or reject it.
              </div>
            </>
          )}
        </div>
      )}

      <div className="border border-slate-800 rounded-md overflow-hidden bg-slate-950/60">
        <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="new-po-all-table">
            <thead className="sticky top-0 bg-slate-900/95 z-10">
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-800">
                <th className="text-left px-3 py-2 font-bold">Uploaded</th>
                <th className="text-left px-3 py-2 font-bold">PO #</th>
                <th className="text-left px-3 py-2 font-bold">Date</th>
                <th className="text-left px-3 py-2 font-bold">Vendor</th>
                <th className="text-left px-3 py-2 font-bold">Outlet</th>
                <th className="text-right px-3 py-2 font-bold">Amount</th>
                <th className="text-center px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Filename</th>
                <th className="text-center px-3 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-slate-500 text-sm" data-testid="new-po-all-empty">
                    {totalCount === 0 ? "No POs uploaded yet." : "No POs match your search."}
                  </td>
                </tr>
              )}
              {items.map((po) => {
                const st = po.chain_status || po.status || "pending";
                return (
                  <tr key={po.id} className="border-b border-slate-900 hover:bg-slate-900/40" data-testid={`new-po-all-row-${po.id}`}>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap font-mono text-[11px]">{fmtDate(po.uploaded_at)}</td>
                    <td className="px-3 py-2 font-mono text-amber-200 whitespace-nowrap">{po.po_number || "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-300 whitespace-nowrap">{fmtDate(po.po_date) || "—"}</td>
                    <td className="px-3 py-2 text-cyan-100 font-semibold max-w-[220px] truncate" title={po.vendor}>{po.vendor || <span className="italic text-slate-500">(missing)</span>}</td>
                    <td className="px-3 py-2 text-slate-300 max-w-[180px] truncate" title={po.outlet}>{po.outlet || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-cyan-100 font-bold whitespace-nowrap">
                      {po.amount != null ? fmtINR(po.amount) : "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Badge className={`text-[9px] uppercase font-bold border ${STATUS_STYLES[st] || STATUS_STYLES.pending}`}>
                        {st === "in_progress" ? "pending" : st}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-500 max-w-[220px] truncate" title={po.filename}>{po.filename || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => onDelete(po)}
                        className="text-slate-600 hover:text-rose-400 p-1"
                        title="Delete PO"
                        data-testid={`new-po-all-delete-${po.id}`}
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onUpload, uploading }) {
  return (
    <div className="border-2 border-dashed border-cyan-900/50 rounded-md p-10 text-center bg-slate-950/50" data-testid="new-po-empty-state">
      <FilePdf size={48} weight="duotone" className="text-rose-400/70 mx-auto mb-3" />
      <div className="text-cyan-100 font-semibold mb-1">No POs uploaded yet</div>
      <div className="text-sm text-slate-400 mb-5">
        Drop a PO PDF — Gemini OCR will auto-fill the approval row.
      </div>
      <Button onClick={onUpload} disabled={uploading} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="new-po-empty-upload-btn">
        <UploadSimple size={14} weight="bold" className="mr-1.5" /> Upload your first PO
      </Button>
    </div>
  );
}

function POListRow({ po, active, onSelect, onDelete }) {
  return (
    <div
      onClick={onSelect}
      className={
        "border rounded-sm px-3 py-2.5 cursor-pointer transition-colors " +
        (active
          ? "border-cyan-500/70 bg-cyan-950/30"
          : "border-slate-800 bg-slate-950/60 hover:bg-slate-900/80 hover:border-slate-700")
      }
      data-testid={`new-po-list-row-${po.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <FilePdf size={13} weight="duotone" className="text-rose-300 shrink-0" />
            <span className="font-semibold text-cyan-50 truncate" title={po.vendor || po.filename}>
              {po.vendor || <span className="italic text-slate-500">(vendor missing)</span>}
            </span>
            <Badge className={`text-[9px] uppercase font-bold border ${STATUS_STYLES[po.status] || STATUS_STYLES.pending}`}>
              {po.status}
            </Badge>
          </div>
          <div className="mt-1 grid grid-cols-3 gap-2 text-[11px]">
            <div className="truncate" title="PO #">
              <span className="text-slate-500">PO </span>
              <span className="font-mono text-amber-200">{po.po_number || "—"}</span>
            </div>
            <div title="Date">
              <span className="text-slate-500">Date </span>
              <span className="font-mono text-slate-300">{fmtDate(po.po_date) || "—"}</span>
            </div>
            <div title="Amount" className="text-right">
              <span className="font-mono tabular-nums text-cyan-100 font-bold">
                {po.amount != null ? fmtINR(po.amount) : "—"}
              </span>
            </div>
          </div>
          <div className="mt-0.5 text-[10px] text-slate-500 truncate">
            {po.outlet || "—"} · {po.filename}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-slate-600 hover:text-rose-400 p-1 shrink-0"
          title="Delete"
          data-testid={`new-po-delete-${po.id}`}
        >
          <Trash size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function POPreviewPanel({ po, onChange }) {
  const [local, setLocal] = useState({});
  useEffect(() => {
    setLocal({
      vendor:    po.vendor    || "",
      po_number: po.po_number || "",
      po_date:   po.po_date   || "",
      amount:    po.amount    != null ? String(po.amount) : "",
      outlet:    po.outlet    || "",
    });
  }, [po.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (field, value) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
  };
  const save = (field) => {
    const v = local[field];
    let toSend = v;
    if (field === "amount") {
      const num = Number(v);
      if (Number.isNaN(num)) { toast.error("Amount must be a number"); return; }
      toSend = num;
    }
    if (toSend === (po[field] ?? (field === "amount" ? null : ""))) return;
    onChange({ [field]: toSend === "" ? null : toSend });
  };

  return (
    <div className="border border-slate-800 rounded-md bg-slate-950/60 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Eye size={14} weight="bold" className="text-cyan-300 shrink-0" />
          <span className="text-xs font-bold text-cyan-100 truncate">{po.filename}</span>
          {po.ocr_error && (
            <Badge className="bg-amber-900/40 text-amber-200 border-amber-700/60 text-[9px] uppercase font-bold gap-1">
              <Warning size={10} weight="fill" /> OCR partial
            </Badge>
          )}
        </div>
        <Badge className={`text-[10px] uppercase font-bold border ${STATUS_STYLES[po.status] || STATUS_STYLES.pending}`}>
          {po.status}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:divide-x md:divide-slate-800">
        {/* PDF iframe */}
        <div className="bg-slate-900 min-h-[520px]" data-testid="new-po-pdf-preview">
          <iframe
            key={po.id}
            src={pdfUrlFor(po)}
            title="PO PDF"
            className="w-full h-[520px]"
            style={{ background: "#1e293b", border: "none" }}
          />
        </div>

        {/* Edit form */}
        <div className="p-4 space-y-3" data-testid="new-po-edit-form">
          <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-300">Extracted fields</div>

          <FieldRow icon={<Storefront size={12} />} label="Vendor">
            <Input
              value={local.vendor || ""}
              onChange={(e) => commit("vendor", e.target.value)}
              onBlur={() => save("vendor")}
              placeholder="Vendor / Supplier name"
              data-testid="new-po-field-vendor"
              className="h-8 text-sm bg-slate-900 border-slate-700"
            />
          </FieldRow>

          <FieldRow icon={<Hash size={12} />} label="PO Number">
            <Input
              value={local.po_number || ""}
              onChange={(e) => commit("po_number", e.target.value)}
              onBlur={() => save("po_number")}
              placeholder="PO-2026-…"
              data-testid="new-po-field-po-number"
              className="h-8 text-sm font-mono bg-slate-900 border-slate-700"
            />
          </FieldRow>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow icon={<Calendar size={12} />} label="Date">
              <Input
                type="date"
                value={local.po_date || ""}
                onChange={(e) => commit("po_date", e.target.value)}
                onBlur={() => save("po_date")}
                data-testid="new-po-field-date"
                className="h-8 text-sm bg-slate-900 border-slate-700"
              />
            </FieldRow>
            <FieldRow icon={<CurrencyInr size={12} />} label="Amount">
              <Input
                type="number"
                step="0.01"
                value={local.amount || ""}
                onChange={(e) => commit("amount", e.target.value)}
                onBlur={() => save("amount")}
                placeholder="0.00"
                data-testid="new-po-field-amount"
                className="h-8 text-sm font-mono tabular-nums bg-slate-900 border-slate-700"
              />
            </FieldRow>
          </div>

          <FieldRow icon={<Storefront size={12} />} label="Outlet (buyer)">
            <Input
              value={local.outlet || ""}
              onChange={(e) => commit("outlet", e.target.value)}
              onBlur={() => save("outlet")}
              placeholder="Mr. Chow's …"
              data-testid="new-po-field-outlet"
              className="h-8 text-sm bg-slate-900 border-slate-700"
            />
          </FieldRow>

          {po.line_items && po.line_items.length > 0 && (
            <div className="pt-3 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-300 mb-2">Line items ({po.line_items.length})</div>
              <div className="max-h-[180px] overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-wider text-slate-400 border-b border-slate-800">
                      <th className="text-left px-2 py-1 font-bold">Product</th>
                      <th className="text-right px-2 py-1 font-bold">Qty</th>
                      <th className="text-right px-2 py-1 font-bold">Rate</th>
                      <th className="text-right px-2 py-1 font-bold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.line_items.map((li, i) => (
                      <tr key={i} className="border-b border-slate-900">
                        <td className="px-2 py-1 text-slate-200 truncate max-w-[160px]" title={li.product}>{li.product || "—"}</td>
                        <td className="px-2 py-1 text-right font-mono tabular-nums text-slate-300">{li.qty ?? "—"}</td>
                        <td className="px-2 py-1 text-right font-mono tabular-nums text-slate-300">{li.rate ?? "—"}</td>
                        <td className="px-2 py-1 text-right font-mono tabular-nums text-cyan-100">{li.amount != null ? fmtINR(li.amount) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="pt-3 border-t border-slate-800 text-[10px] text-slate-500 flex items-center justify-between">
            <span>Uploaded {fmtDate(po.uploaded_at)} by {po.uploaded_by || "—"}</span>
            <span className="inline-flex items-center gap-1">
              <FloppyDisk size={10} weight="bold" /> auto-saves on blur
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ icon, label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1 mb-1">
        <span className="text-cyan-400">{icon}</span> {label}
      </Label>
      {children}
    </div>
  );
}
